import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { AccessLevel } from '@/entities/vfo/types';
import { Checkbox } from '@/features/vfo/components/Checkbox';
import { VfoFileIcon } from '@/features/vfo/components/VfoFileIcon';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useIsFounder } from '@/features/vfo/hooks/useIsFounder';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { usePermissions } from '@/features/vfo/hooks/usePermissions';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { useNotices } from '@/shared/providers/notices';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SelectDocumentsToCaseModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';

export const SelectDocumentsToCaseModal: FC<SelectDocumentsToCaseModalProps> = ({
  caseId,
  onFinish,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addDocumentsToCase' });
  const { t: tf } = useTranslation('features/vfo', { keyPrefix: 'taskManager' });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const { currentUser } = useMembers();
  const { cases, attachDocument } = useCases();
  const { documents } = useDocuments();
  const { hasAnyRole } = usePermissions();
  const modals = useModalsStore();
  const isDesktop = useIsDesktop();
  const isFounder = useIsFounder();
  const notices = useNotices();

  const handleToggle = (id: string) => {
    setSelectedFiles((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };

  const handleAdd = async () => {
    setIsLoading(true);
    await Promise.allSettled(selectedFiles.map((item) => attachDocument(caseId, item)));
    setIsLoading(false);
    modals.closeModal();
    notices.success(tf(selectedFiles.length > 1 ? 'documentsAddedPlural' : 'documentsAdded'));
    onFinish?.();
  };

  const availableDocuments = useMemo(() => {
    if (!caseId) {
      return documents;
    }
    const currentCase = cases.find((c) => c.id === caseId);
    const documentIdsInCase = new Set(currentCase?.caseDocuments?.map((dc) => dc.documentId) || []);
    const documentsNotInCase = documents.filter((doc) => !documentIdsInCase.has(doc.id));
    if (!isFounder && !hasAnyRole([AccessLevel.DELEGATED, AccessLevel.EXECUTIVE])) {
      return documentsNotInCase.filter(
        (item) => item.caseMember.profileId === currentUser?.profileId,
      );
    }
    return documentsNotInCase;
  }, [documents, cases, caseId]);

  useEffect(() => {
    setInitialized(true);
    if (availableDocuments.length || initialized) return;

    handleCreate();
  }, [availableDocuments, initialized]);

  const handleCreate = () => {
    modals.closeModal();

    const type = currentUser?.isFounder ? 'AddVfoDocumentModal' : 'UploadVfoDocumentModal';
    modals.openModal({ type, caseId, onFinish });
  };

  if (!availableDocuments.length) {
    return null;
  }

  return (
    <ModalContainerV1 title={t('title')}>
      <ListContainer>
        {isDesktop ? (
          <span style={{ fontSize: 14 }}>{t('text')}</span>
        ) : (
          <Button
            style={{ marginBottom: 16 }}
            size="middle"
            type="default"
            onClick={() => {
              modals.closeModal();
              modals.openModal({
                type: 'UploadVfoDocumentModal',
                caseId,
              });
            }}
          >
            {' '}
            {t('uploadManually')}
          </Button>
        )}
        {availableDocuments.map((file) => (
          <ListItem key={file.id} onClick={() => handleToggle(file.id)}>
            <Checkbox checked={selectedFiles.includes(file.id)} />
            <FileTypeWrapper className={cn({ isExternal: file.source === 'EXTERNAL' })}>
              <VfoFileIcon file={file} size="s20" />
            </FileTypeWrapper>
            <Text>{file.title}</Text>
          </ListItem>
        ))}
      </ListContainer>
      <Buttons>
        {isDesktop && (
          <Button size="middle" type="text" onClick={handleCreate}>
            {t('addNewBtn')}
          </Button>
        )}
        <Button
          size="middle"
          onClick={handleAdd}
          disabled={selectedFiles.length === 0 || isLoading}
          loading={isLoading}
        >
          {t('addBtn')}
        </Button>
      </Buttons>
    </ModalContainerV1>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 24px;

  ${media.md}, ${breakpoints.sm} {
    padding: 16px;
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 64px;
  gap: 8px;
  flex-shrink: 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    margin-top: auto;
  }
`;

const FileTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.systemDark200};
  border-radius: 4px;
  width: 28px;
  height: 36px;
  color: ${COLORS.white};
  position: relative;
  flex-shrink: 0;

  &.isExternal {
    background: ${COLORS.systemSky300};
  }
`;

const Text = styled.span`
  font-size: 12px;
  line-height: 16px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-height: 40px;
`;
